<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownContainer from 'dashboard/components-next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'dashboard/components-next/dropdown-menu/base/DropdownBody.vue';
import DropdownSection from 'dashboard/components-next/dropdown-menu/base/DropdownSection.vue';
import DropdownItem from 'dashboard/components-next/dropdown-menu/base/DropdownItem.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import SectionLayout from './components/SectionLayout.vue';
import AccountSettingsHeader from './components/AccountSettingsHeader.vue';
import SpecificSettings from './Specific.vue';

defineProps({
  embedded: { type: Boolean, default: false },
});

const { locale: i18nLocale, t } = useI18n();
const { uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { currentAccount, updateAccount } = useAccount();

const getUIFlags = useMapGetter('accounts/getUIFlags');
const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');

const name = ref('');
const locale = ref('en');
const domain = ref('');
const supportEmail = ref('');
const features = ref({});

const rules = computed(() => ({
  name: { required },
  locale: { required },
}));

const v$ = useVuelidate(rules, {
  name,
  locale,
});

const uiFlags = computed(() => getUIFlags.value);

const languagesSortedByCode = computed(() =>
  [...enabledLanguages].sort((l1, l2) =>
    l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
  )
);
const selectedLanguage = computed(() =>
  languagesSortedByCode.value.find(lang => lang.iso_639_1_code === locale.value)
);
const selectLanguage = code => {
  locale.value = code;
  v$.value.locale.$touch();
};

const isUpdating = computed(() => uiFlags.value.isUpdating);

const featureInboundEmailEnabled = computed(
  () => !!features.value?.inbound_emails
);

const featureCustomReplyDomainEnabled = computed(
  () =>
    featureInboundEmailEnabled.value && !!features.value?.custom_reply_domain
);

const featureCustomReplyEmailEnabled = computed(() => {
  return (
    featureInboundEmailEnabled.value && !!features.value?.custom_reply_email
  );
});

const syncAccount = account => {
  if (!account?.id) return;

  i18nLocale.value = uiSettings.value?.locale || account.locale;
  name.value = account.name;
  locale.value = account.locale;
  domain.value = account.domain;
  supportEmail.value = account.support_email;
  features.value = account.features || {};
};

watch(currentAccount, account => syncAccount(account), {
  deep: true,
  immediate: true,
});

const updateAccountSettings = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('GENERAL_SETTINGS.FORM.ERROR'));
    return;
  }

  try {
    await updateAccount({
      locale: locale.value,
      name: name.value,
      domain: domain.value,
      support_email: supportEmail.value,
    });

    i18nLocale.value = uiSettings.value?.locale || locale.value;
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col max-w-2xl mx-auto w-full gap-6">
    <AccountSettingsHeader v-if="!embedded" />

    <div class="flex-grow flex-shrink min-w-0">
      <SectionLayout
        :title="$t('ORGANIZED_SETTINGS.ACCOUNT_BASICS')"
        :description="$t('ORGANIZED_SETTINGS.ACCOUNT_BASICS_DESCRIPTION')"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccountSettings"
        >
          <WithLabel
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <DropdownContainer class="w-full">
              <template #trigger="{ isOpen, toggle }">
                <button
                  type="button"
                  class="flex items-center justify-between w-full min-h-10 gap-3 px-3 border rounded-lg border-n-weak bg-n-field text-sm text-left text-n-slate-12 hover:border-n-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :aria-expanded="isOpen"
                  :aria-label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
                  aria-haspopup="listbox"
                  @click="toggle"
                >
                  <span>{{ selectedLanguage?.name || locale }}</span>
                  <span
                    :class="
                      isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
                    "
                    class="shrink-0 text-base text-n-slate-11"
                  />
                </button>
              </template>
              <DropdownBody class="z-50 w-full min-w-56">
                <DropdownSection height="max-h-64">
                  <DropdownItem
                    v-for="lang in languagesSortedByCode"
                    :key="lang.iso_639_1_code"
                    :click="() => selectLanguage(lang.iso_639_1_code)"
                  >
                    <span
                      class="flex items-center justify-between w-full gap-3"
                    >
                      {{ lang.name }}
                      <span
                        v-if="locale === lang.iso_639_1_code"
                        class="text-base i-lucide-check text-n-brand"
                      />
                    </span>
                  </DropdownItem>
                </DropdownSection>
              </DropdownBody>
            </DropdownContainer>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyDomainEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domain"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <template #help>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}

              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </template>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyEmailEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <NextInput
              v-model="supportEmail"
              type="text"
              class="w-full"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </WithLabel>
          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>

    <SpecificSettings v-if="!embedded" section="appearance" embedded />
    <AccountId v-if="!embedded" />
    <div v-if="!embedded && !uiFlags.isFetchingItem && isOnChatwootCloud">
      <AccountDelete />
    </div>
    <BuildInfo v-if="!embedded" />
  </div>
</template>
